/**
 * PWA — SERVICE WORKER, CONECTIVIDAD Y ACTUALIZACIONES (FASE 4.5)
 *
 * Mismo patrón que `cuandoCaduqueLaSesion` en `api.ts`: un pub-sub
 * mínimo, sin Context, para un estado transversal que no depende de
 * sesión ni de ruta. Ningún componente decide nada aquí — sólo leen
 * `useConectividad()`/`useActualizacionPwa()`.
 *
 * FRONTERA CON FASE 5 (encargo, sección 8): esto es la "abstracción
 * mínima de conectividad" que pide el encargo, y nada más. No hay cola,
 * no hay IndexedDB de negocio, no hay reintento — sólo saber si hay red
 * y si hay una versión nueva esperando. Cualquier tentación de guardar
 * aquí una mutación fallida "para más tarde" es Fase 5, no esto.
 */
import { useEffect, useRef, useState } from 'react';

import { observarRed } from './api.ts';

// ------------------------------------------------------------ conectividad

export type Conectividad = 'ONLINE' | 'OFFLINE';

/**
 * F14.1 — CONEXIÓN REAL CON EL SERVIDOR
 *
 * `navigator.onLine` sólo dice si hay interfaz de red: una wifi sin salida a Internet, un portal cautivo o el
 * servidor caído dan `true`. La conexión operativa se comprueba contra `GET /api/salud` (sin sesión, sin base de
 * datos, una línea): sólo una respuesta JSON de NeatOS cuenta como servidor alcanzable.
 *
 *   SIN_RED                  `navigator.onLine === false` (sin comprobar nada más)
 *   SERVIDOR_NO_DISPONIBLE   hay red pero NeatOS no responde (timeout, error de red, portal cautivo, 502…)
 *   ONLINE                   NeatOS responde
 *   DESCONOCIDO              aún sin comprobar (se usa `navigator.onLine` como señal provisional)
 *
 * Sin bombardear: una comprobación al arrancar el monitor, al cambiar `online`/`offline`, al volver a primer plano,
 * cuando una petición de la API falla por red, y periódica (60 s en línea; 15 s → 2 min, creciente, sin conexión),
 * sólo con la pestaña visible. Una petición de la API que llega al servidor ya demuestra conexión: no hace falta
 * comprobar nada más.
 */
export type EstadoConexion = 'ONLINE' | 'SIN_RED' | 'SERVIDOR_NO_DISPONIBLE' | 'DESCONOCIDO';

let estadoReal: EstadoConexion = 'DESCONOCIDO';
const escuchasConexion = new Set<(e: EstadoConexion) => void>();

function fijarEstadoReal(e: EstadoConexion): void {
  if (e === estadoReal) return;
  const antes = estadoReal;
  estadoReal = e;
  for (const f of escuchasConexion) f(e);
  //  Volver a tener servidor (desde sin red o servidor caído) dispara la sincronización pendiente (`offline/sync.ts`).
  if (e === 'ONLINE' && antes !== 'ONLINE' && antes !== 'DESCONOCIDO') window.dispatchEvent(new Event('neatos:conexion-recuperada'));
}

export function estadoConexionReal(): EstadoConexion {
  return estadoReal;
}

function estadoConectividad(): Conectividad {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'OFFLINE';
  //  Mientras no se ha comprobado, `navigator.onLine` manda (nunca se bloquea el arranque esperando al servidor).
  if (estadoReal === 'DESCONOCIDO') return 'ONLINE';
  return estadoReal === 'ONLINE' ? 'ONLINE' : 'OFFLINE';
}

const TIEMPO_MAXIMO_SALUD_MS = 5_000;

/** Una comprobación real. Exportada para las pruebas y para «Comprobar ahora». */
export async function comprobarConexion(): Promise<EstadoConexion> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    fijarEstadoReal('SIN_RED');
    return 'SIN_RED';
  }
  const control = new AbortController();
  const limite = setTimeout(() => control.abort(), TIEMPO_MAXIMO_SALUD_MS);
  try {
    const r: unknown = await fetch('/api/salud', { cache: 'no-store', signal: control.signal });
    //  Un doble de pruebas que no devuelve una Response no dice nada: se deja como estaba.
    if (!(r instanceof Response)) return estadoReal;
    let cuerpo: unknown = null;
    try {
      cuerpo = JSON.parse(await r.text());
    } catch {
      cuerpo = null;
    }
    //  NeatOS responde con JSON propio (`{ok: true}`, o un error de la API con `code`); un portal cautivo o la
    //  página de error de un proxy, no.
    const deNeatos = typeof cuerpo === 'object' && cuerpo !== null && ('ok' in cuerpo || 'code' in cuerpo);
    const e: EstadoConexion = deNeatos && r.status < 500 ? 'ONLINE' : 'SERVIDOR_NO_DISPONIBLE';
    fijarEstadoReal(e);
    return e;
  } catch {
    const e: EstadoConexion = typeof navigator !== 'undefined' && navigator.onLine === false ? 'SIN_RED' : 'SERVIDOR_NO_DISPONIBLE';
    fijarEstadoReal(e);
    return e;
  } finally {
    clearTimeout(limite);
  }
}

let monitorActivo = false;
let temporizadorMonitor: ReturnType<typeof setTimeout> | null = null;
let comprobacionesFallidas = 0;

function programarComprobacion(): void {
  if (temporizadorMonitor !== null) clearTimeout(temporizadorMonitor);
  const espera = estadoReal === 'ONLINE' ? 60_000 : Math.min(120_000, 15_000 * 2 ** Math.min(comprobacionesFallidas, 3));
  temporizadorMonitor = setTimeout(() => {
    if (document.visibilityState !== 'visible') {
      programarComprobacion();
      return;
    }
    void comprobarYProgramar();
  }, espera);
}

async function comprobarYProgramar(): Promise<void> {
  const e = await comprobarConexion();
  comprobacionesFallidas = e === 'ONLINE' ? 0 : comprobacionesFallidas + 1;
  programarComprobacion();
}

/**
 * Arranca el monitor (una vez, desde `main.tsx`). Las pruebas de componentes no lo arrancan: sin él, la
 * conectividad es `navigator.onLine`, como antes de F14.1.
 */
export function iniciarMonitorConexion(): void {
  if (monitorActivo || typeof window === 'undefined') return;
  monitorActivo = true;
  window.addEventListener('online', () => void comprobarYProgramar());
  window.addEventListener('offline', () => {
    fijarEstadoReal('SIN_RED');
    programarComprobacion();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void comprobarYProgramar();
  });
  observarRed((llego) => {
    //  Una respuesta real del servidor ya es la prueba; un fallo de red pide comprobar (sin esperar al periodo).
    if (llego) {
      comprobacionesFallidas = 0;
      fijarEstadoReal('ONLINE');
    } else if (estadoReal === 'ONLINE' || estadoReal === 'DESCONOCIDO') {
      void comprobarYProgramar();
    }
  });
  void comprobarYProgramar();
}

/** Estado real de la conexión (para el indicador global y el centro de sincronización). */
export function useConexionReal(): EstadoConexion {
  const [estado, setEstado] = useState<EstadoConexion>(estadoReal);
  useEffect(() => {
    escuchasConexion.add(setEstado);
    setEstado(estadoReal);
    return () => {
      escuchasConexion.delete(setEstado);
    };
  }, []);
  return estado;
}

export function useConectividad(): Conectividad {
  const [estado, setEstado] = useState<Conectividad>(estadoConectividad);
  useEffect(() => {
    function actualizar(): void {
      setEstado(estadoConectividad());
    }
    window.addEventListener('online', actualizar);
    window.addEventListener('offline', actualizar);
    escuchasConexion.add(actualizar);
    //  La primera comprobación real puede terminar entre el render y esta suscripción (recarga sin red: el fallo de
    //  `/api/salud` es inmediato): sin releer aquí, la pantalla se quedaba «en línea» hasta el siguiente cambio.
    actualizar();
    return () => {
      window.removeEventListener('online', actualizar);
      window.removeEventListener('offline', actualizar);
      escuchasConexion.delete(actualizar);
    };
  }, []);
  return estado;
}

/**
 * FASE 5.7-C C02 — variante de `useConectividad()` para decidir si HAY QUE
 * REDIRIGIR a `/offline*` al ENTRAR a una pantalla. Congela el valor del
 * primer render (nunca se actualiza después): E2E `pwa.mjs` §9 dejó claro
 * que una pantalla ya abierta (p.ej. un formulario de NC a medio rellenar)
 * NUNCA debe navegar sola en cuanto la conexión cae en directo — eso es
 * justo lo que ya cubre el propio fallo de red de esa acción (`api.ts` →
 * `FalloApi SIN_CONEXION`), mostrado en la misma pantalla. Sólo importa
 * el estado de conectividad EN EL INSTANTE de entrar; un cambio posterior
 * mientras la pantalla sigue montada no debe mover a nadie de sitio.
 */
export function useConectividadAlEntrar(): Conectividad {
  const conectividad = useConectividad();
  return useRef(conectividad).current;
}

// ------------------------------------------------------------- actualización

type Escucha = () => void;
const alHaberActualizacion = new Set<Escucha>();
let workerEnEspera: ServiceWorker | null = null;
/** F14.1 — el usuario pulsó «Actualizar»: sólo entonces se recarga al cambiar de worker. */
let actualizacionPedida = false;

function avisarActualizacionDisponible(): void {
  for (const f of alHaberActualizacion) f();
}

/**
 * Registra el service worker y arma la detección de "hay una versión
 * nueva esperando". Se llama UNA vez, en el arranque (`main.tsx`) —nunca
 * desde un componente—, para que no dependa de qué pantalla esté
 * montada en cada momento.
 *
 * Nunca activa la versión nueva por su cuenta: eso sólo lo hace
 * `actualizar()` (devuelto por `useActualizacionPwa()`), y sólo cuando
 * el usuario pulsa el botón — nunca a mitad de una acción operativa.
 */
export function registrarServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js')
      .then((registro) => {
        //  Ya había una esperando cuando esta pestaña se abrió: otra
        //  pestaña la dejó a medias, o es de una sesión anterior.
        if (registro.waiting !== null && navigator.serviceWorker.controller !== null) {
          workerEnEspera = registro.waiting;
          avisarActualizacionDisponible();
        }

        registro.addEventListener('updatefound', () => {
          const nuevo = registro.installing;
          if (nuevo === null) return;
          nuevo.addEventListener('statechange', () => {
            //  `controller !== null` es la clave: si todavía no hay
            //  controller, esto es la instalación INICIAL (la propia
            //  carga ya trae esta versión) — no hay nada que "actualizar".
            if (nuevo.state === 'installed' && navigator.serviceWorker.controller !== null) {
              workerEnEspera = nuevo;
              avisarActualizacionDisponible();
            }
          });
        });
      })
      .catch(() => {
        //  Un SW que no llega a registrarse no debe romper la app: PWA es
        //  una mejora progresiva, nunca un requisito para funcionar.
      });

    //  Cuando el worker en espera toma el control, recarga UNA vez — y SÓLO si lo pidió el usuario con
    //  `actualizar()`. F14.1: antes recargaba con cualquier `controllerchange`, también el de la PRIMERA
    //  instalación (`clients.claim()`), que recargaba la página sola en la primera visita, a mitad de lo que fuera.
    navigator.serviceWorker.addEventListener('controllerchange', () => alCambiarControlador());
  });
}

let yaRecargo = false;
/**
 * F14.1 — qué hacer cuando un worker nuevo toma el control: recargar UNA vez si (y sólo si) el usuario pidió
 * actualizar. Exportada para las pruebas (`recargar` sustituible).
 */
export function alCambiarControlador(recargar: () => void = () => window.location.reload()): boolean {
  if (yaRecargo || !actualizacionPedida) return false;
  yaRecargo = true;
  recargar();
  return true;
}

export function useActualizacionPwa(): { disponible: boolean; actualizar: () => void } {
  const [disponible, setDisponible] = useState(workerEnEspera !== null);

  useEffect(() => {
    function alAvisar(): void {
      setDisponible(true);
    }
    alHaberActualizacion.add(alAvisar);
    return () => {
      alHaberActualizacion.delete(alAvisar);
    };
  }, []);

  return {
    disponible,
    actualizar: () => {
      actualizacionPedida = true;
      workerEnEspera?.postMessage({ type: 'NEATOS_SALTAR_ESPERA' });
    },
  };
}
